import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import type { PointsOutletContext } from './ModernPointsLayout'
import { fireRedEnvelopeSwal } from './redEnvelopeSwal'
import {
  RE_SPRITE_CELL_H,
  RE_SPRITE_CELL_W,
  RE_SPRITE_COLS,
  RE_SPRITE_FONT,
  RE_SPRITE_H,
  RE_SPRITE_W,
  delay,
  envelopeClosed,
  envelopeOpen,
  rollValueToTarget,
} from './redEnvelopeDemoUtils'
import {
  playRedEnvelopeClick,
  playRedEnvelopeOpen,
  playRedEnvelopeRollTick,
  playRedEnvelopeSummary,
} from './redEnvelopeDemoSounds'
import {
  fetchRedEnvelopePlayConfig,
  fetchRedEnvelopeRoundStatus,
  openRedEnvelopeCell,
  revealRemainingRedEnvelope,
  startRedEnvelopeRound,
  type RedEnvelopeMode,
  type RedEnvelopePaymentType,
} from '../../services/redEnvelopeService'
import './redEnvelopeDemo.css'

const ASSET = '/demo/red-envelope'

type Phase = 'intro' | 'play' | 'summary'

type CellVisual = 'closed' | 'wiggle' | 'open'

type CellState = {
  index: number
  opened: boolean
  amount: number | null
  auto?: boolean
  visual: CellVisual
  /** sprite-font roll while opening */
  rollDisplay: number | null
}

const MODE_OPTIONS = [
  { opens: 1, label: 'ซอง 1 ใบ' },
  { opens: 3, label: 'ซอง 3 ใบ' },
  { opens: 5, label: 'ซอง 5 ใบ' },
  { opens: 9, label: 'ซอง 9 ใบ' },
] as const

function emptyCells(): CellState[] {
  return Array.from({ length: 9 }, (_, i) => ({
    index: i + 1,
    opened: false,
    amount: null,
    visual: 'closed' as const,
    rollDisplay: null,
  }))
}

function randomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function mockPrizeForOpen(opensLeftBefore: number, letterIndex: number) {
  void letterIndex
  const band = opensLeftBefore >= 7 ? [8, 35] : opensLeftBefore >= 4 ? [12, 55] : [18, 100]
  return randomInt(band[0], band[1])
}

function mockAutoRevealAmount() {
  return randomInt(5, 40)
}

function SpriteDigits({
  value,
  scale = 0.52,
  layout = 'cell',
}: {
  value: number
  scale?: number
  layout?: 'cell' | 'summary'
}) {
  const chars = String(Math.max(0, value)).split('')
  return (
    <div
      className={`red-env-demo__digits${layout === 'summary' ? ' red-env-demo__digits--summary' : ''}`}
      style={{ ['--re-digit-scale' as string]: scale }}
    >
      {chars.map((ch, i) => {
        const n = Number(ch)
        if (Number.isNaN(n)) return null
        const col = n % RE_SPRITE_COLS
        const row = n < 5 ? 0 : 1
        return (
          <span
            key={`${i}-${ch}`}
            className="red-env-demo__digit"
            style={{
              width: RE_SPRITE_CELL_W,
              height: RE_SPRITE_CELL_H,
              backgroundImage: `url(${RE_SPRITE_FONT})`,
              backgroundSize: `${RE_SPRITE_W}px ${RE_SPRITE_H}px`,
              backgroundPosition: `-${col * RE_SPRITE_CELL_W}px -${row * RE_SPRITE_CELL_H}px`,
            }}
          />
        )
      })}
    </div>
  )
}

const RedEnvelopeDemoPage: React.FC<{ variant?: 'demo' | 'live' }> = ({ variant = 'demo' }) => {
  const isLive = variant === 'live'
  const outlet = useOutletContext<PointsOutletContext>()
  const pointsBalance = outlet?.pointsBalance ?? 0
  const cashBalance = outlet?.cashBalance ?? 0
  const refreshUserSummary = outlet?.refreshUserSummary

  const [phase, setPhase] = useState<Phase>('intro')
  const [modeOpens, setModeOpens] = useState<number>(3)
  const [modeKey, setModeKey] = useState('mode_3')
  const [paymentType, setPaymentType] = useState<RedEnvelopePaymentType>('points')
  const [apiModes, setApiModes] = useState<RedEnvelopeMode[]>([])
  const [roundId, setRoundId] = useState<string | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [opensRemaining, setOpensRemaining] = useState(0)
  const [totalEarned, setTotalEarned] = useState(0)
  const [cells, setCells] = useState<CellState[]>(emptyCells)
  const [busy, setBusy] = useState(false)
  const [summaryRoll, setSummaryRoll] = useState(0)
  const [soundOn, setSoundOn] = useState(true)
  const [paymentAutoPicked, setPaymentAutoPicked] = useState(false)

  const rewardUnit = paymentType === 'cash' ? 'บาท' : 'คะแนน'

  useEffect(() => {
    if (!isLive) return
    let cancelled = false
    void (async () => {
      try {
        const res = await fetchRedEnvelopePlayConfig()
        if (cancelled) return
        const modes = res.data?.modes || []
        setApiModes(modes)
        if (modes.length > 0) {
          const first = modes[0]
          setModeOpens(first.envelopeCount)
          setModeKey(first.key)
        }
        setConfigError(null)
      } catch (e) {
        if (!cancelled) {
          setConfigError(e instanceof Error ? e.message : 'โหลดเกมไม่สำเร็จ')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isLive])

  const modeOptions = useMemo(() => {
    if (isLive && apiModes.length > 0) {
      return apiModes.map((m) => ({
        opens: m.envelopeCount,
        label: m.label,
        key: m.key,
        costPoints: m.costPoints,
        costCash: m.costCash,
      }))
    }
    return MODE_OPTIONS.map((m) => ({ ...m, key: `mode_${m.opens}`, costPoints: 0, costCash: 0 }))
  }, [apiModes, isLive])

  const selectedMode = useMemo(
    () => modeOptions.find((m) => m.key === modeKey) || modeOptions.find((m) => m.opens === modeOpens) || modeOptions[0],
    [modeKey, modeOpens, modeOptions],
  )

  const entryCost = useMemo(() => {
    if (!selectedMode) return 0
    return paymentType === 'cash' ? Number(selectedMode.costCash) || 0 : Number(selectedMode.costPoints) || 0
  }, [paymentType, selectedMode])

  const activeBalance = paymentType === 'cash' ? cashBalance : pointsBalance
  const canAfford = !isLive || entryCost <= 0 || activeBalance >= entryCost
  /** ปิดเฉพาะตอน busy — อย่า disable เพราะยอดไม่พอ ไม่งั้นกดแล้วไม่มี Swal */
  const canClickStart = !busy
  const canStartClean = !busy && (!isLive || (apiModes.length > 0 && canAfford && !configError))

  // ถ้าคะแนนไม่พอแต่เงินพอ — สลับไปจ่ายเงินอัตโนมัติครั้งแรก (pilot UX)
  useEffect(() => {
    if (!isLive || paymentAutoPicked || apiModes.length === 0) return
    const mode = apiModes.find((m) => m.key === modeKey) || apiModes[0]
    if (!mode) return
    const needPoints = Number(mode.costPoints) || 0
    const needCash = Number(mode.costCash) || 0
    if (pointsBalance < needPoints && cashBalance >= needCash && needCash > 0) {
      setPaymentType('cash')
      setPaymentAutoPicked(true)
    }
  }, [apiModes, cashBalance, isLive, modeKey, paymentAutoPicked, pointsBalance])

  const showActionError = useCallback(async (message: string) => {
    setActionError(message)
    await fireRedEnvelopeSwal({
      title: 'เริ่มเกมไม่ได้',
      message,
      tone: 'warning',
    })
  }, [])

  const resetCells = useCallback(() => {
    setCells(emptyCells())
  }, [])

  const abortCancelledRound = useCallback(
    async (message?: string) => {
      setBusy(false)
      setPhase('intro')
      setRoundId(null)
      setOpensRemaining(0)
      setTotalEarned(0)
      setSummaryRoll(0)
      resetCells()
      await fireRedEnvelopeSwal({
        title: 'รอบถูกยกเลิก',
        message:
          message ||
          'แอดมินยกเลิกรอบนี้แล้ว ค่าเข้ารอบถูกคืนแล้ว — ไม่สามารถเปิดซองหรือรับรางวัลต่อได้',
        tone: 'warning',
      })
      await refreshUserSummary?.()
    },
    [refreshUserSummary, resetCells],
  )

  const isCancelledError = (e: unknown) => {
    const err = e as { code?: string; message?: string; status?: number }
    return (
      err?.code === 'ROUND_CANCELLED' ||
      err?.status === 409 ||
      /ยกเลิก/.test(String(err?.message || ''))
    )
  }

  // ระหว่างเล่น — เช็คสถานะรอบเป็นระยะ ถ้า admin ยกเลิกให้หยุดทันที
  useEffect(() => {
    if (!isLive || phase !== 'play' || !roundId) return
    let stopped = false
    const tick = async () => {
      try {
        const res = await fetchRedEnvelopeRoundStatus(roundId)
        if (stopped) return
        if (res.data.status === 'cancelled') {
          await abortCancelledRound()
        }
      } catch {
        // ignore transient errors
      }
    }
    void tick()
    const id = window.setInterval(() => void tick(), 4000)
    return () => {
      stopped = true
      window.clearInterval(id)
    }
  }, [abortCancelledRound, isLive, phase, roundId])

  const patchCell = useCallback((index: number, patch: Partial<CellState>) => {
    setCells((prev) => prev.map((c) => (c.index === index ? { ...c, ...patch } : c)))
  }, [])

  const animateOpenCell = useCallback(
    async (index: number, prize: number, auto?: boolean) => {
      if (soundOn) playRedEnvelopeOpen()
      patchCell(index, { visual: 'wiggle', rollDisplay: null })
      await delay(420)
      patchCell(index, { visual: 'open', rollDisplay: 0 })
      let tick = 0
      await rollValueToTarget(prize, (v) => {
        tick += 1
        if (soundOn && tick % 2 === 0) playRedEnvelopeRollTick()
        patchCell(index, { rollDisplay: v })
      })
      patchCell(index, {
        opened: true,
        amount: prize,
        auto,
        rollDisplay: prize,
        visual: 'open',
      })
    },
    [patchCell, soundOn],
  )

  const startGame = useCallback(async () => {
    setActionError(null)
    if (isLive) {
      if (apiModes.length === 0) {
        await showActionError(configError || 'ยังโหลดโหมดเกมไม่ได้ กรุณารีเฟรชหน้า')
        return
      }
      if (!canAfford) {
        await showActionError(
          paymentType === 'cash'
            ? `ยอดเงินไม่พอ (ต้องการ ${entryCost.toLocaleString('th-TH')} บาท · คงเหลือ ${cashBalance.toLocaleString('th-TH')} บาท)`
            : `คะแนนไม่พอ (ต้องการ ${entryCost.toLocaleString('th-TH')} คะแนน · คงเหลือ ${pointsBalance.toLocaleString('th-TH')} คะแนน) — สลับเป็น「จ่ายเงิน」หรือไปแลกคะแนนก่อน`,
        )
        return
      }

      const unit = paymentType === 'cash' ? 'บาท' : 'คะแนน'
      const confirm = await fireRedEnvelopeSwal({
        title: 'ยืนยันเริ่มรอบซองแดง',
        message: `หัก ${entryCost.toLocaleString('th-TH')} ${unit} เพื่อเปิด ${selectedMode?.label || 'ซอง'} — ต้องการเริ่มเลยไหม?`,
        tone: 'confirm',
        showCancel: true,
        confirmText: 'เริ่มเกม',
        cancelText: 'ยกเลิก',
      })
      if (!confirm.isConfirmed) return
    }

    resetCells()
    setTotalEarned(0)
    if (isLive) {
      setBusy(true)
      try {
        const res = await startRedEnvelopeRound(modeKey, paymentType)
        setRoundId(res.data.roundId)
        setOpensRemaining(res.data.opensRemaining)
        setModeOpens(res.data.envelopeCount)
        setPhase('play')
        await refreshUserSummary?.()
      } catch (e) {
        await showActionError(e instanceof Error ? e.message : 'เริ่มรอบไม่สำเร็จ')
      } finally {
        setBusy(false)
      }
      return
    }
    setOpensRemaining(modeOpens)
    setPhase('play')
  }, [
    apiModes.length,
    canAfford,
    cashBalance,
    configError,
    entryCost,
    isLive,
    modeKey,
    modeOpens,
    paymentType,
    pointsBalance,
    refreshUserSummary,
    resetCells,
    selectedMode?.label,
    showActionError,
  ])

  const finishWithAutoReveal = useCallback(
    async (currentCells: CellState[], earned: number) => {
      setBusy(true)
      let sum = earned
      if (isLive && roundId) {
        try {
          const res = await revealRemainingRedEnvelope(roundId)
          const byIndex = new Map(res.data.cells.map((c) => [c.index, c.amount]))
          for (let i = 0; i < currentCells.length; i++) {
            if (currentCells[i].opened) continue
            const amt = byIndex.get(currentCells[i].index) ?? 0
            await animateOpenCell(currentCells[i].index, amt, true)
            sum = res.data.totalEarned
            setTotalEarned(sum)
          }
          await refreshUserSummary?.()
        } catch (e) {
          setBusy(false)
          if (isCancelledError(e)) {
            await abortCancelledRound(e instanceof Error ? e.message : undefined)
            return
          }
          await fireRedEnvelopeSwal({
            title: 'สรุปรอบไม่สำเร็จ',
            message: e instanceof Error ? e.message : 'สรุปรอบไม่สำเร็จ',
            tone: 'error',
          })
          return
        }
      } else {
        const next = [...currentCells]
        for (let i = 0; i < next.length; i++) {
          if (next[i].opened) continue
          const amt = mockAutoRevealAmount()
          await animateOpenCell(next[i].index, amt, true)
          sum += amt
          setTotalEarned(sum)
        }
      }
      await delay(500)
      setBusy(false)
      setPhase('summary')
      setSummaryRoll(0)
      if (soundOn) playRedEnvelopeSummary()
      let summaryTick = 0
      await rollValueToTarget(sum, (v) => {
        summaryTick += 1
        if (soundOn && summaryTick % 3 === 0) playRedEnvelopeRollTick()
        setSummaryRoll(v)
      })

      const unit = paymentType === 'cash' ? 'บาท' : 'คะแนน'
      await fireRedEnvelopeSwal({
        title: 'จบรอบแล้ว',
        message: `คุณได้รับรวม ${sum.toLocaleString('th-TH')} ${unit} จากรอบนี้`,
        tone: 'success',
        confirmText: 'เยี่ยม!',
      })
    },
    [abortCancelledRound, animateOpenCell, isLive, paymentType, refreshUserSummary, roundId, soundOn],
  )

  const onCellClick = useCallback(
    async (letterIndex: number) => {
      if (phase !== 'play' || busy) return
      const cell = cells.find((c) => c.index === letterIndex)
      if (!cell || cell.opened || cell.visual !== 'closed') return
      if (opensRemaining <= 0) return

      setBusy(true)
      if (soundOn) playRedEnvelopeClick()

      let prize: number
      if (isLive && roundId) {
        try {
          const res = await openRedEnvelopeCell(roundId, letterIndex)
          prize = res.data.amount
          const nextOpens = res.data.opensRemaining
          await animateOpenCell(letterIndex, prize)
          const nextEarned = res.data.totalEarned
          setOpensRemaining(nextOpens)
          setTotalEarned(nextEarned)
          setBusy(false)
          if (nextOpens === 0) {
            setCells((current) => {
              void finishWithAutoReveal(current, nextEarned)
              return current
            })
          }
          return
        } catch (e) {
          setBusy(false)
          if (isCancelledError(e)) {
            await abortCancelledRound(e instanceof Error ? e.message : undefined)
            return
          }
          await fireRedEnvelopeSwal({
            title: 'เปิดซองไม่สำเร็จ',
            message: e instanceof Error ? e.message : 'เปิดซองไม่สำเร็จ',
            tone: 'error',
          })
          return
        }
      }

      prize = mockPrizeForOpen(opensRemaining, letterIndex)
      const nextOpens = opensRemaining - 1

      await animateOpenCell(letterIndex, prize)
      const nextEarned = totalEarned + prize
      setOpensRemaining(nextOpens)
      setTotalEarned(nextEarned)
      setBusy(false)

      if (nextOpens === 0) {
        setCells((current) => {
          void finishWithAutoReveal(current, nextEarned)
          return current
        })
      }
    },
    [abortCancelledRound, animateOpenCell, busy, cells, finishWithAutoReveal, isLive, opensRemaining, phase, roundId, soundOn, totalEarned],
  )

  const hintCell = useMemo(() => {
    if (phase !== 'play' || opensRemaining <= 0 || busy) return null
    const closed = cells.filter((c) => c.visual === 'closed' && !c.opened)
    if (closed.length === 0) return null
    return closed[Math.floor(Math.random() * closed.length)]?.index ?? null
  }, [busy, cells, opensRemaining, phase])

  return (
    <div className="red-env-demo">
      <div className="red-env-demo__banner">
        {isLive
          ? 'เกมส์ซองแดง · หัก/จ่ายจริงตามโหมด (คะแนนหรือเงิน)'
          : 'ตัวอย่าง UI (React + asset C3) · ยังไม่หักคะแนน / ไม่เรียก API'}
        {configError && isLive ? ` · ${configError}` : null}
        <button
          type="button"
          className="red-env-demo__sound-toggle"
          onClick={() => setSoundOn((v) => !v)}
        >
          {soundOn ? '🔊 เสียงเปิด' : '🔇 เสียงปิด'}
        </button>
      </div>

      <div className="red-env-demo__stage">
        <div
          className="red-env-demo__bg"
          style={{
            backgroundImage: `url(${ASSET}/${phase === 'summary' ? 'bg-end.png' : 'bg-preview.png'})`,
          }}
        />
        <div className="red-env-demo__inner">
          <img className="red-env-demo__logo red-env-demo__logo--float" src={`${ASSET}/logo.png`} alt="เกมส์ซองแดง" />

          {phase === 'intro' && (
            <>
              <h2 className="red-env-demo__title">{isLive ? 'เปิดซองรับรางวัล' : 'เปิดซองรับคะแนน'}</h2>
              <p className="red-env-demo__subtitle">
                {isLive
                  ? 'เลือกจ่ายด้วยคะแนนหรือเงิน — ผลสุ่มจาก server'
                  : 'ใช้ซองจาก C3 + ตัวเลข sprite เดิม (มี animation เปิดซอง / ไล่เลข)'}
              </p>
              {isLive && (
                <div className="red-env-demo__history-row">
                  <Link to="/history/games/red-envelope" className="red-env-demo__history-btn">
                    <i className="fas fa-history" aria-hidden />
                    ประวัติการเล่นซองแดง
                  </Link>
                </div>
              )}
              {isLive && (
                <div className="red-env-demo__modes" style={{ marginBottom: '0.75rem' }}>
                  <button
                    type="button"
                    className={`red-env-demo__mode${paymentType === 'points' ? ' red-env-demo__mode--active' : ''}`}
                    onClick={() => {
                      setPaymentType('points')
                      setActionError(null)
                    }}
                  >
                    จ่ายคะแนน
                  </button>
                  <button
                    type="button"
                    className={`red-env-demo__mode${paymentType === 'cash' ? ' red-env-demo__mode--active' : ''}`}
                    onClick={() => {
                      setPaymentType('cash')
                      setActionError(null)
                    }}
                  >
                    จ่ายเงิน (บาท)
                  </button>
                </div>
              )}
              <div className="red-env-demo__stats">
                <div className="red-env-demo__stat">
                  <span className="red-env-demo__stat-label">
                    {paymentType === 'cash' ? 'เงินคงเหลือ' : 'คะแนนคงเหลือ'}
                    {isLive ? '' : ' (mock)'}
                  </span>
                  <span className="red-env-demo__stat-value">
                    {activeBalance.toLocaleString('th-TH', {
                      minimumFractionDigits: paymentType === 'cash' ? 2 : 0,
                      maximumFractionDigits: paymentType === 'cash' ? 2 : 0,
                    })}
                  </span>
                </div>
                <div className="red-env-demo__stat">
                  <span className="red-env-demo__stat-label">{isLive ? 'ค่าเข้ารอบ' : 'ค่าเข้า demo'}</span>
                  <span className="red-env-demo__stat-value">
                    {isLive
                      ? entryCost.toLocaleString('th-TH')
                      : '1,000'}
                  </span>
                </div>
              </div>
              {isLive && !canAfford && (
                <div className="red-env-demo__afford-warn" role="alert">
                  <p>
                    {paymentType === 'cash'
                      ? `ยอดเงินไม่พอสำหรับโหมดนี้ (ต้องการ ${entryCost.toLocaleString('th-TH')} บาท)`
                      : `คะแนนไม่พอสำหรับโหมดนี้ (ต้องการ ${entryCost.toLocaleString('th-TH')} คะแนน)`}
                  </p>
                  <div className="red-env-demo__afford-actions">
                    {paymentType === 'points' && cashBalance >= entryCost ? (
                      <button
                        type="button"
                        className="red-env-demo__afford-btn"
                        onClick={() => {
                          setPaymentType('cash')
                          setActionError(null)
                        }}
                      >
                        สลับเป็นจ่ายเงิน
                      </button>
                    ) : null}
                    {paymentType === 'points' ? (
                      <Link to="/points/moneytopoint" className="red-env-demo__afford-link">
                        ไปแลกเงินเป็นคะแนน
                      </Link>
                    ) : (
                      <Link to="/topup" className="red-env-demo__afford-link">
                        ไปเติมเงิน
                      </Link>
                    )}
                  </div>
                </div>
              )}
              {actionError && isLive ? (
                <p className="red-env-demo__action-error" role="alert">
                  {actionError}
                </p>
              ) : null}
              <p className="red-env-demo__subtitle" style={{ marginBottom: '0.65rem' }}>
                เลือกจำนวนครั้งเปิด (โหมดเกม)
              </p>
              <div className="red-env-demo__modes">
                {modeOptions.map((m) => (
                  <button
                    key={m.key || m.opens}
                    type="button"
                    className={`red-env-demo__mode${modeOpens === m.opens ? ' red-env-demo__mode--active' : ''}`}
                    onClick={() => {
                      setModeOpens(m.opens)
                      if (m.key) setModeKey(m.key)
                      setActionError(null)
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="red-env-demo__actions">
                <button
                  type="button"
                  className={`red-env-demo__start${canStartClean ? ' red-env-demo__start--pulse' : ' red-env-demo__start--disabled'}`}
                  disabled={!canClickStart}
                  onClick={() => void startGame()}
                >
                  <img src={`${ASSET}/btn-start.png`} alt={busy ? 'กำลังเริ่ม...' : 'เริ่มเกม'} />
                </button>
                {busy ? <p className="red-env-demo__busy-hint">กำลังเริ่มรอบ...</p> : null}
              </div>
            </>
          )}

          {phase === 'play' && (
            <>
              <h2 className="red-env-demo__title">เลือกซองที่อยากเปิด</h2>
              <p className="red-env-demo__subtitle">
                เหลือ {opensRemaining} ครั้ง · รวม {totalEarned.toLocaleString('th-TH')} {rewardUnit}
              </p>
              <div className="red-env-demo__stats">
                <div className="red-env-demo__stat">
                  <span className="red-env-demo__stat-label">ครั้งที่เหลือ</span>
                  <span className="red-env-demo__stat-value">{opensRemaining}</span>
                </div>
                <div className="red-env-demo__stat">
                  <span className="red-env-demo__stat-label">ได้แล้ว</span>
                  <span className="red-env-demo__stat-value">{totalEarned.toLocaleString('th-TH')}</span>
                </div>
              </div>
              <div className="red-env-demo__grid">
                {cells.map((cell) => {
                  const isHint = hintCell === cell.index
                  const showOpen = cell.visual === 'open' || cell.opened
                  const src = showOpen ? envelopeOpen(cell.index) : envelopeClosed(cell.index)
                  const showDigits = cell.rollDisplay != null && (cell.visual === 'open' || cell.opened)
                  return (
                    <button
                      key={cell.index}
                      type="button"
                      className={[
                        'red-env-demo__cell',
                        isHint ? 'red-env-demo__cell--pulse' : '',
                        cell.visual === 'wiggle' ? 'red-env-demo__cell--wiggle' : '',
                        cell.visual === 'open' && cell.rollDisplay != null ? 'red-env-demo__cell--pop' : '',
                        cell.auto ? 'red-env-demo__cell--auto' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      disabled={cell.opened || cell.visual !== 'closed' || opensRemaining <= 0 || busy}
                      onClick={() => void onCellClick(cell.index)}
                      aria-label={`ซองหมายเลข ${cell.index}`}
                    >
                      <img className="red-env-demo__envelope" src={src} alt="" />
                      {showDigits && <SpriteDigits value={cell.rollDisplay ?? 0} scale={0.58} />}
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {phase === 'summary' && (
            <div className="red-env-demo__summary">
              <div className="red-env-demo__summary-panel">
                <img src={`${ASSET}/popup-result.png`} alt="" className="red-env-demo__summary-frame" />
                <div className="red-env-demo__summary-digits-wrap red-env-demo__summary-digits-wrap--card">
                  <SpriteDigits
                    value={summaryRoll || totalEarned}
                    scale={0.72}
                    layout="summary"
                  />
                </div>
                <div className="red-env-demo__summary-amount-strip" aria-hidden>
                  <SpriteDigits
                    value={summaryRoll || totalEarned}
                    scale={0.44}
                    layout="summary"
                  />
                </div>
              </div>
              <p className="red-env-demo__summary-label">
                {isLive ? `รวมจากรอบนี้ (${rewardUnit})` : 'คะแนนรวมจากรอบนี้ (demo)'}
              </p>
              <div className="red-env-demo__actions">
                <button
                  type="button"
                  className="red-env-demo__start"
                  onClick={() => {
                    setPhase('intro')
                    setSummaryRoll(0)
                    setRoundId(null)
                    resetCells()
                  }}
                >
                  <img src={`${ASSET}/btn-start.png`} alt="เล่นอีกครั้ง" />
                </button>
                <Link to="/history/games/red-envelope" className="red-env-demo__history-btn">
                  <i className="fas fa-history" aria-hidden />
                  ดูประวัติซองแดง
                </Link>
                <Link to="/points/rewards" className="red-env-demo__link">
                  ไปแลกของรางวัล
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {!isLive && (
        <div className="red-env-demo__compare">
          <strong>แก้แล้ว:</strong> หน้าสรุปไม่ใช้ <code>letterprizereward.png</code> (เป็น sprite 0–9 เหมือนกัน) แล้ว —
          แสดงเฉพาะยอดรวมบนกรอบ <code>popup-result</code> · ตัวเลขซอง/สถิติใหญ่ขึ้น · เสียงจาก C3 (คลิก/เปิด/ไล่เลข/สรุป)
        </div>
      )}
    </div>
  )
}

export default RedEnvelopeDemoPage
